/**
 * CancelOrderDialog — customer-facing cancel with transparent fee + refund disclosure.
 * Fetches a live quote (free / 20% / 45%) then confirms. For post-pickup cancels it
 * explains the return flow (driver brings it back, sender confirms with a return PIN).
 */
import { useState, useEffect } from "react";
import { AlertTriangle, Loader2, Undo2 } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import api from "../lib/api";

const money = (n) => `J$${Number(n || 0).toLocaleString()}`;

const CancelOrderDialog = ({ packageId, onDone, onClose }) => {
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [reason, setReason] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/packages/${packageId}/cancel-quote`);
        setQuote(res.data);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Could not load cancellation details");
        onClose?.();
      } finally {
        setLoading(false);
      }
    })();
  }, [packageId, onClose]);

  const confirm = async () => {
    setSubmitting(true);
    try {
      const res = await api.post(`/packages/${packageId}/cancel-order`, { reason });
      if (res.data?.requires_return) {
        toast.success("Cancelled. The driver will return your package — check your email for the return PIN.");
      } else {
        toast.success(`Cancelled. ${money(res.data?.refund_amount)} will be refunded to your card.`);
      }
      onDone?.(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not cancel");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4" data-testid="cancel-order-dialog">
      <div className="w-full max-w-md rounded-2xl bg-slate-900 border border-slate-700 p-6 space-y-4">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-amber-400" />
          <h3 className="text-white font-bold text-lg">Cancel this delivery?</h3>
        </div>

        {loading ? (
          <div className="py-8 flex justify-center"><Loader2 className="h-6 w-6 text-emerald-400 animate-spin" /></div>
        ) : !quote?.can_cancel ? (
          <>
            <p className="text-slate-300 text-sm" data-testid="cancel-not-allowed">{quote?.reason || "This delivery can no longer be cancelled."}</p>
            <Button onClick={onClose} className="w-full bg-slate-700 hover:bg-slate-600 text-white">Close</Button>
          </>
        ) : (
          <>
            <p className="text-slate-400 text-sm">{quote.note}</p>
            <div className="rounded-xl bg-slate-800/70 border border-slate-700 divide-y divide-slate-700">
              <Row label="Delivery price" value={money(quote.price)} />
              <Row label={`Cancellation fee${quote.fee_percent ? ` (${quote.fee_percent}%)` : ""}`}
                   value={quote.fee_amount ? `− ${money(quote.fee_amount)}` : "Free"}
                   valueClass={quote.fee_amount ? "text-red-400" : "text-emerald-400"} />
              <Row label="Refund to your card" value={money(quote.refund_amount)} valueClass="text-emerald-400 font-bold" bold />
            </div>

            {quote.requires_return && (
              <div className="rounded-lg bg-amber-500/10 border border-amber-500/40 p-3 text-xs text-amber-200 flex gap-2">
                <Undo2 className="h-4 w-4 shrink-0 mt-0.5" />
                <span>The driver will bring the package back to you. We'll email you a <b>return PIN</b> — give it to the driver on hand-back to complete the return.</span>
              </div>
            )}

            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Reason (optional)"
              data-testid="cancel-reason-input"
              className="w-full rounded-lg bg-slate-800 border border-slate-600 px-3 py-2 text-white text-sm"
            />

            <div className="flex gap-2">
              <Button onClick={onClose} variant="outline" className="flex-1 border-slate-600 text-slate-200" data-testid="cancel-dialog-keep">
                Keep delivery
              </Button>
              <Button onClick={confirm} disabled={submitting} className="flex-1 bg-red-600 hover:bg-red-700 text-white" data-testid="cancel-dialog-confirm">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm cancel"}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const Row = ({ label, value, valueClass = "text-white", bold }) => (
  <div className="flex items-center justify-between px-4 py-2.5">
    <span className={`text-sm ${bold ? "text-slate-200" : "text-slate-400"}`}>{label}</span>
    <span className={`text-sm ${valueClass}`}>{value}</span>
  </div>
);

export default CancelOrderDialog;
